<template>
    <div class="min-h-screen bg-cover bg-center bg-no-repeat relative overflow-hidden" style="background-image: url(/images/bg-damage.jpg);">

        <!-- Mesh Gradient Overlay -->
        <div class="absolute inset-0">
            <div class="absolute inset-0 bg-gradient-to-br from-black/95 via-slate-900/90 to-black/95"></div>
            <div class="absolute top-0 left-0 w-[500px] h-[500px] bg-blue-600/20 rounded-full filter blur-[120px] animate-blob"></div>
            <div class="absolute bottom-0 right-0 w-[600px] h-[600px] bg-cyan-500/15 rounded-full filter blur-[120px] animate-blob animation-delay-2000"></div>
            <div class="absolute top-1/2 left-1/2 w-[400px] h-[400px] bg-amber-500/10 rounded-full filter blur-[100px] animate-blob animation-delay-4000"></div>
        </div>

        <!-- Grid Pattern Overlay -->
        <div class="absolute inset-0 opacity-[0.03]" style="background-image: linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px); background-size: 50px 50px;"></div>

        <!-- Floating Elements -->
        <div class="absolute inset-0 overflow-hidden pointer-events-none">
            <div class="floating-shape shape-1"></div>
            <div class="floating-shape shape-2"></div>
            <div class="floating-shape shape-3"></div>
            <div class="floating-ring ring-1"></div>
            <div class="floating-ring ring-2"></div>
        </div>

        <!-- Main Content -->
        <div class="relative z-10 min-h-screen flex">

            <!-- Left Side - Premium Branding -->
            <div class="hidden lg:flex lg:w-[55%] flex-col justify-between p-12 xl:p-20">
                <!-- Top Navigation Style -->
                <div class="flex items-center justify-between animate-fade-down">
                    <img class="h-12 w-auto filter brightness-0 invert opacity-90" src="/images/logo.svg" alt="74Auto">
                    <div class="flex items-center space-x-2 px-4 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm">
                        <span class="relative flex h-2 w-2">
                            <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                            <span class="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                        </span>
                        <span class="text-white/70 text-xs font-medium tracking-wide">DEALER PORTAL</span>
                    </div>
                </div>

                <!-- Center Hero Content -->
                <div class="space-y-10 animate-fade-right">
                    <!-- Greeting Badge -->
                    <div class="inline-block">
                        <div class="text-amber-400/90 text-sm font-semibold tracking-[0.2em] uppercase mb-4">
                            <span v-if="hour < 12">Good Morning</span>
                            <span v-else-if="hour < 18">Good Afternoon</span>
                            <span v-else>Good Evening</span>
                        </div>
                    </div>

                    <!-- Main Headline -->
                    <div class="space-y-4">
                        <h1 class="text-6xl xl:text-7xl font-bold text-white leading-tight tracking-tight">
                            <span class="block text-white/90">Automotive</span>
                            <span class="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 pb-2">Dealer Management</span>
                            <span class="block text-white/90">System</span>
                        </h1>
                    </div>

                    <!-- Subtext -->
                    <p class="text-lg text-white/50 max-w-lg leading-relaxed font-light">
                        Streamline your dealership operations with comprehensive vehicle tracking, sales management, and real-time inventory insights.
                    </p>

                    <!-- Feature Cards -->
                    <div class="grid grid-cols-3 gap-4 pt-6">
                        <div class="feature-card group">
                            <div class="p-5 rounded-2xl bg-white/[0.03] border border-white/[0.08] backdrop-blur-sm hover:bg-white/[0.06] hover:border-white/[0.15] transition-all duration-500">
                                <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-500">
                                    <svg class="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
                                    </svg>
                                </div>
                                <div class="text-2xl font-bold text-white mb-1">Inventory</div>
                                <div class="text-white/40 text-xs">Vehicle Tracking</div>
                            </div>
                        </div>
                        <div class="feature-card group">
                            <div class="p-5 rounded-2xl bg-white/[0.03] border border-white/[0.08] backdrop-blur-sm hover:bg-white/[0.06] hover:border-white/[0.15] transition-all duration-500">
                                <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-500">
                                    <svg class="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                    </svg>
                                </div>
                                <div class="text-2xl font-bold text-white mb-1">Sales</div>
                                <div class="text-white/40 text-xs">Deal Management</div>
                            </div>
                        </div>
                        <div class="feature-card group">
                            <div class="p-5 rounded-2xl bg-white/[0.03] border border-white/[0.08] backdrop-blur-sm hover:bg-white/[0.06] hover:border-white/[0.15] transition-all duration-500">
                                <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-500">
                                    <svg class="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                    </svg>
                                </div>
                                <div class="text-2xl font-bold text-white mb-1">Reports</div>
                                <div class="text-white/40 text-xs">Business Insights</div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Bottom Footer -->
                <div class="flex items-center justify-between animate-fade-up">
                    <div class="text-white/30 text-sm">
                        &copy; {{ new Date().getFullYear() }} 74 Autos
                    </div>
                    <a href="https://xelent.pk" target="_blank" class="flex items-center space-x-2 text-white/30 hover:text-white/60 transition-colors duration-300 group">
                        <span class="text-sm">Crafted by</span>
                        <span class="text-sm font-medium text-white/50 group-hover:text-cyan-400 transition-colors">Xelent Solutions</span>
                    </a>
                </div>
            </div>

            <!-- Right Side - Login Panel -->
            <div class="w-full lg:w-[45%] flex items-center justify-center p-6 sm:p-8 lg:p-12">
                <div class="w-full max-w-[420px]">

                    <!-- Login Card -->
                    <div class="login-card relative">
                        <!-- Card Glow -->
                        <div class="absolute -inset-px bg-gradient-to-b from-white/20 via-white/5 to-transparent rounded-[28px] pointer-events-none"></div>
                        <div class="absolute -inset-[2px] bg-gradient-to-b from-blue-500/20 to-transparent rounded-[30px] blur-xl opacity-50"></div>

                        <!-- Card Content -->
                        <div class="relative bg-gradient-to-b from-white/[0.12] to-white/[0.04] backdrop-blur-2xl rounded-[26px] p-8 sm:p-10 border border-white/[0.1] shadow-2xl">

                            <!-- Mobile Logo -->
                            <div class="lg:hidden text-center mb-8">
                                <img class="h-10 w-auto mx-auto filter brightness-0 invert opacity-90" src="/images/logo.svg" alt="74Auto">
                            </div>

                            <!-- Header -->
                            <div class="text-center mb-10">
                                <h2 class="text-3xl font-bold text-white mb-2 tracking-tight">Welcome Back</h2>
                                <p class="text-white/40 text-sm">Enter your credentials to continue</p>
                            </div>

                            <!-- Error Messages -->
                            <div v-if="errors" class="mb-8 animate-shake">
                                <div class="p-4 rounded-xl bg-red-950/90 border border-red-500/40">
                                    <div class="flex items-start space-x-3">
                                        <div class="w-8 h-8 rounded-lg bg-red-500/30 flex items-center justify-center flex-shrink-0">
                                            <svg class="w-4 h-4 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                                                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
                                            </svg>
                                        </div>
                                        <div>
                                            <p class="text-red-400 font-semibold text-sm">Authentication Error</p>
                                            <ul class="mt-1.5 space-y-1">
                                                <li v-for="error in errors" :key="error" class="text-red-300 text-xs">{{ error }}</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Form -->
                            <form id="loginForm" @submit.prevent="refreshCsrfToken" class="space-y-6">
                                <!-- Email Field -->
                                <div class="space-y-2">
                                    <label for="email" class="flex items-center justify-between">
                                        <span class="text-white/60 text-sm font-medium">Email Address</span>
                                    </label>
                                    <div class="relative flex items-center">
                                        <div class="absolute left-4 text-white/30">
                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                                            </svg>
                                        </div>
                                        <input
                                            type="email"
                                            name="email"
                                            id="email"
                                            placeholder="you@company.com"
                                            class="w-full pl-12 pr-4 py-4 bg-white/[0.05] border border-white/[0.08] rounded-xl text-white placeholder-white/25 focus:outline-none focus:border-white/20 focus:bg-white/[0.08] transition-all duration-300 text-sm"
                                            required
                                            autofocus
                                            v-model="form.email"
                                        >
                                    </div>
                                </div>

                                <!-- Password Field -->
                                <div class="space-y-2">
                                    <label for="password" class="flex items-center justify-between">
                                        <span class="text-white/60 text-sm font-medium">Password</span>
                                    </label>
                                    <div class="relative flex items-center">
                                        <div class="absolute left-4 text-white/30">
                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                                            </svg>
                                        </div>
                                        <input
                                            type="password"
                                            id="password"
                                            name="password"
                                            autocomplete="current-password"
                                            placeholder="Enter your password"
                                            class="w-full pl-12 pr-4 py-4 bg-white/[0.05] border border-white/[0.08] rounded-xl text-white placeholder-white/25 focus:outline-none focus:border-white/20 focus:bg-white/[0.08] transition-all duration-300 text-sm"
                                            required
                                            v-model="form.password"
                                        >
                                    </div>
                                </div>

                                <!-- Submit Button -->
                                <div class="pt-2">
                                    <button
                                        type="submit"
                                        class="w-full relative overflow-hidden group rounded-xl"
                                        :disabled="loading"
                                    >
                                        <!-- Button Content -->
                                        <div class="flex items-center justify-center py-4 px-6 bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-500 rounded-xl font-semibold text-white shadow-lg shadow-blue-500/25 group-hover:shadow-blue-500/40 transition-all duration-300 group-active:scale-[0.98]">
                                            <span v-if="loading" class="flex items-center">
                                                <svg class="animate-spin mr-3 h-5 w-5" fill="none" viewBox="0 0 24 24">
                                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Authenticating...
                                            </span>
                                            <span v-else class="flex items-center">
                                                Access Dashboard
                                                <svg class="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                                                </svg>
                                            </span>
                                        </div>
                                    </button>
                                </div>
                            </form>

                            <!-- Divider -->
                            <div class="relative my-8">
                                <div class="absolute inset-0 flex items-center">
                                    <div class="w-full border-t border-white/[0.06]"></div>
                                </div>
                                <div class="relative flex justify-center">
                                    <span class="px-4 text-xs text-white/30 bg-transparent">PROTECTED ACCESS</span>
                                </div>
                            </div>

                            <!-- Security Info -->
                            <div class="flex items-center justify-center space-x-6 text-white/30">
                                <div class="flex items-center space-x-2">
                                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"/>
                                    </svg>
                                    <span class="text-xs">SSL Secured</span>
                                </div>
                                <div class="w-1 h-1 rounded-full bg-white/20"></div>
                                <div class="flex items-center space-x-2">
                                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                                    </svg>
                                    <span class="text-xs">256-bit Encryption</span>
                                </div>
                            </div>

                            <!-- Mobile Footer -->
                            <div class="lg:hidden mt-8 pt-6 border-t border-white/[0.06] text-center">
                                <p class="text-white/30 text-xs">
                                    Powered by <a href="https://xelent.pk" target="_blank" class="text-white/50 hover:text-cyan-400 transition-colors">Xelent Solutions</a>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

import axios from 'axios';
import { getLoginUser } from "./../queries/remarketing/user.graphql";

export default {
  data(){
      return{
        loading:0,
        hour:new Date().getHours(),
        form:{
            email:null,
            password:null,
            acceptJson:'1',
        },
        errors:null,
      }
  },
  created(){
        localStorage.clear();
  },
  methods: {
      showPassword() {
            if ($("#show-password").attr('data-status') == '1') {
                $("#password").attr('type', 'text');
                $("#show-password").addClass('fa-eye-slash')
                $("#show-password").removeClass('fa-eye')
                $("#show-password").attr('data-status', '0')
            } else {
                $("#password").attr('type', 'password');
                $("#show-password").removeClass('fa-eye-slash')
                $("#show-password").addClass('fa-eye');
                $("#show-password").attr('data-status', '1')
            }
        },
        refreshCsrfToken() {
            this.loading = 1;
            fetch('/refresh-csrf')
                .then(response => response.json())
                .then(data => {
                const csrfToken = document.querySelector('meta[name="csrf-token"]');
                csrfToken.setAttribute('content', data.token);
                const csrfInputs = document.querySelectorAll('input[name="_token"]');
                csrfInputs.forEach(input => input.value = data.token);

                // Clear previous errors
                this.errors = null;

                // Make login request
                axios.post('/api/login', this.form)
                    .then(res => {
                    if (res.data) {
                        this.$apollo
                        .query({
                            query: getLoginUser,
                            variables:{
                                currentUser:'1'
                            }
                        })
                        .then(res => {
                            localStorage.setItem('current_user', JSON.stringify(res.data.loginUser[0]))
                            let current_role = JSON.parse(localStorage.getItem('current_user')).role.toString();
                            if(current_role == '4'){
                                window.location.href = '/admin/saleman/website';
                            }
                            else if(current_role == '3'){
                                window.location.href = '/admin/checkin';
                            }
                            else if(current_role == '2' || current_role == '4' || current_role == '5' || current_role == '6' || current_role == '8' || current_role == '9'){
                                window.location.href = '/admin/dashboard';
                            }
                            else if(current_role == '7'){
                                window.location.href = '/admin/banker';
                            }
                            else if(current_role == '9'){
                                window.location.href = '/admin/received';
                            }
                            else if(current_role == '10'){
                                window.location.href = '/admin/pictures';
                            }
                            else{
                                window.location.href = '/admin/dashboard';
                            }
                        })
                        .catch(err => {
                            this.loading = 0;
                            console.error('GraphQL Error:', err);
                            this.errors = ['Error retrieving user permissions. Please try again.'];
                        });
                    }
                    })
                    .catch(err => {
                        this.loading = 0;
                        console.error('Login Error:', err);

                        if (err.response && err.response.data && err.response.data.errors) {
                            // Capture validation or server errors
                            this.errors = Object.values(err.response.data.errors).flat();
                        } else {
                            this.errors = ['An unexpected error occurred. Please try again.'];
                        }
                    });
                })
                .catch(error => {
                    this.loading = 0;
                    console.error('Error refreshing CSRF token:', error);
                    this.errors = ['Error refreshing CSRF token. Please try again.'];
                });
        }
  },
};
</script>
<style scoped>
/* Blob Animation */
@keyframes blob {
    0%, 100% {
        transform: translate(0, 0) scale(1);
    }
    25% {
        transform: translate(20px, -30px) scale(1.1);
    }
    50% {
        transform: translate(-20px, 20px) scale(0.9);
    }
    75% {
        transform: translate(30px, 10px) scale(1.05);
    }
}

.animate-blob {
    animation: blob 20s ease-in-out infinite;
}

.animation-delay-2000 {
    animation-delay: 2s;
}

.animation-delay-4000 {
    animation-delay: 4s;
}

/* Floating Shapes */
.floating-shape {
    position: absolute;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(6, 182, 212, 0.05));
    animation: floatShape 25s ease-in-out infinite;
}

.shape-1 {
    width: 300px;
    height: 300px;
    top: 10%;
    right: 10%;
    animation-delay: 0s;
}

.shape-2 {
    width: 200px;
    height: 200px;
    bottom: 20%;
    left: 5%;
    animation-delay: 5s;
}

.shape-3 {
    width: 150px;
    height: 150px;
    top: 50%;
    right: 30%;
    animation-delay: 10s;
}

/* Floating Rings */
.floating-ring {
    position: absolute;
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 50%;
    animation: floatRing 30s linear infinite;
}

.ring-1 {
    width: 400px;
    height: 400px;
    top: -100px;
    right: -100px;
}

.ring-2 {
    width: 600px;
    height: 600px;
    bottom: -200px;
    left: -200px;
    animation-direction: reverse;
    filter: blur(8px);
}

@keyframes floatShape {
    0%, 100% {
        transform: translate(0, 0) rotate(0deg);
        opacity: 0.5;
    }
    25% {
        transform: translate(30px, -40px) rotate(90deg);
        opacity: 0.3;
    }
    50% {
        transform: translate(-20px, 30px) rotate(180deg);
        opacity: 0.5;
    }
    75% {
        transform: translate(40px, 20px) rotate(270deg);
        opacity: 0.3;
    }
}

@keyframes floatRing {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}

/* Fade Animations */
@keyframes fadeDown {
    from {
        opacity: 0;
        transform: translateY(-30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes fadeRight {
    from {
        opacity: 0;
        transform: translateX(-50px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
}

@keyframes fadeUp {
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes shake {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
    20%, 40%, 60%, 80% { transform: translateX(4px); }
}

.animate-fade-down {
    animation: fadeDown 0.8s ease-out both;
}

.animate-fade-right {
    animation: fadeRight 1s ease-out 0.2s both;
}

.animate-fade-up {
    animation: fadeUp 0.8s ease-out 0.4s both;
}

.animate-shake {
    animation: shake 0.5s ease-in-out;
}

/* Login Card */
.login-card {
    animation: fadeUp 0.8s ease-out both;
}

/* Feature Card Hover */
.feature-card {
    transition: transform 0.3s ease;
}

.feature-card:hover {
    transform: translateY(-4px);
}

/* Button Styling */
button {
    outline: none;
    border: none;
}

button:focus {
    outline: none;
    box-shadow: none;
}

button:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

/* Input Styling */
input::placeholder {
    transition: opacity 0.3s ease;
}

input:focus::placeholder {
    opacity: 0.5;
}

/* Scrollbar */
::-webkit-scrollbar {
    width: 6px;
}

::-webkit-scrollbar-track {
    background: transparent;
}

::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.2);
}
</style>